import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { addDoc, collection, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { SERVICE_CATEGORIES } from "./serviceCatalog";
import { BOOKING_SLOT_OPTIONS, buildDateSlotKey, parseDateSlotKey } from "./bookingSlots";
import VetOnboardingPanel from "./VetOnboardingPanel";
import SubscriptionCheckoutButton from "./SubscriptionCheckoutButton";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

export default function ProviderDashboard({
  email,
  providerId,
  providerStatus,
  onLogout,
  onAddService,
  onMyServices,
  onBookings,
  onEarnings,
  onPayouts,
  onReviews,
  onMarketplace,
  onSupport,
}) {
  const status = providerStatus || "pending";
  const canAddService = status === "approved";
  const [unavailableDate, setUnavailableDate] = useState("");
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [unavailableSlotDate, setUnavailableSlotDate] = useState("");
  const [unavailableSlotValue, setUnavailableSlotValue] = useState(BOOKING_SLOT_OPTIONS[0]);
  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [profileDraft, setProfileDraft] = useState({
    displayName: "",
    businessName: "",
    about: "",
    primaryCategory: "",
    yearsExperience: "",
    avgResponseMinutes: "",
  });
  const [myServices, setMyServices] = useState([]);
  const [featuredServiceId, setFeaturedServiceId] = useState("");
  const [requestingFeatured, setRequestingFeatured] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [summary, setSummary] = useState({
    totalServices: 0,
    liveServices: 0,
    activeBookings: 0,
    completedBookings: 0,
    grossCollected: 0,
    platformCommission: 0,
    estimatedPayout: 0,
    avgRating: 0,
    reviewCount: 0,
  });
  const [checklist, setChecklist] = useState({
    profileComplete: false,
    serviceAdded: false,
    availabilityUpdated: false,
    approved: false,
  });
  const [providerDocData, setProviderDocData] = useState({});

  useEffect(() => {
    const loadProfile = async () => {
      if (!providerId) {
        setUnavailableDates([]);
        setProviderDocData({});
        return;
      }
      try {
        setLoading(true);
        setLoadError("");
        const snap = await getDoc(doc(db, "users", providerId));
        if (!snap.exists()) {
          setUnavailableDates([]);
          setUnavailableSlots([]);
          setProviderDocData({});
          return;
        }
        const data = snap.data() || {};
        setProviderDocData(data);
        setUnavailableDates(Array.isArray(data.unavailableDates) ? data.unavailableDates : []);
        setUnavailableSlots(Array.isArray(data.unavailableSlots) ? data.unavailableSlots : []);
        setProfileDraft({
          displayName: data.displayName || "",
          businessName: data.businessName || "",
          about: data.about || "",
          primaryCategory: data.primaryCategory || "",
          yearsExperience: data.yearsExperience || "",
          avgResponseMinutes: data.avgResponseMinutes || "",
        });

        const servicesSnap = await getDocs(query(
          collection(db, "services"),
          where("providerId", "==", providerId),
        ));
        const list = servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMyServices(list);
        setFeaturedServiceId((prev) => prev || (list[0]?.id || ""));
        const [bookingsById, bookingsByEmail, reviewsSnap] = await Promise.all([
          getDocs(query(collection(db, "bookings"), where("providerId", "==", providerId))),
          email
            ? getDocs(query(collection(db, "bookings"), where("providerEmail", "==", email)))
            : Promise.resolve({ docs: [] }),
          email
            ? getDocs(query(collection(db, "reviews"), where("providerEmail", "==", email)))
            : Promise.resolve({ docs: [] }),
        ]);
        const mergedBookings = new Map();
        [...bookingsById.docs, ...bookingsByEmail.docs].forEach((item) => {
          mergedBookings.set(item.id, item.data());
        });
        const bookingList = Array.from(mergedBookings.values());
        const activeBookings = bookingList.filter((b) => ["accepted", "in_progress", "pending", "pending_assignment"].includes(b.status)).length;
        const completedBookings = bookingList.filter((b) => String(b.status || "") === "completed").length;
        const settledPaid = bookingList
          .filter((b) => b.paymentStatus === "paid" && b.refundStatus !== "refund_processed")
          .map((b) => ({
            amount: Number(b.amount) || 0,
            commission: Number(b.commissionAmount) || (Number(b.amount) || 0) * 0.2,
            payout: Number(b.providerEarning) || (Number(b.amount) || 0) * 0.8,
          }));
        const grossCollected = settledPaid.reduce((sum, b) => sum + b.amount, 0);
        const platformCommission = settledPaid.reduce((sum, b) => sum + b.commission, 0);
        const estimatedPayout = settledPaid.reduce((sum, b) => sum + b.payout, 0);
        const reviewList = reviewsSnap.docs
          .map((item) => item.data() || {})
          .filter((review) => review.hidden !== true);
        const validRatings = reviewList
          .map((r) => Number(r.rating))
          .filter((r) => Number.isFinite(r) && r >= 1 && r <= 5);
        const reviewCount = validRatings.length;
        const avgRating = reviewCount ? validRatings.reduce((sum, r) => sum + r, 0) / reviewCount : 0;
        setSummary({
          totalServices: list.length,
          liveServices: list.filter((s) => s.isPublic === true && s.providerStatusSnapshot === "approved").length,
          activeBookings,
          completedBookings,
          grossCollected: Math.round(grossCollected),
          platformCommission: Math.round(platformCommission),
          estimatedPayout: Math.round(estimatedPayout),
          avgRating,
          reviewCount,
        });
        const profileComplete = Boolean(
          String(data.displayName || "").trim() &&
          String(data.businessName || "").trim() &&
          String(data.about || "").trim(),
        );
        setChecklist({
          profileComplete,
          serviceAdded: list.length > 0,
          availabilityUpdated:
            (Array.isArray(data.unavailableDates) ? data.unavailableDates.length : 0) > 0 ||
            (Array.isArray(data.unavailableSlots) ? data.unavailableSlots.length : 0) > 0,
          approved: (data.providerStatus || "") === "approved",
        });
      } catch (error) {
        setLoadError(error.message || "Failed to load provider dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [providerId, email, reloadKey]);

  const syncAvailabilityToServices = async (nextDates, nextSlots) => {
    if (!providerId) return;
    const servicesSnap = await getDocs(query(
      collection(db, "services"),
      where("providerId", "==", providerId),
    ));
    await Promise.all(
      servicesSnap.docs.map((serviceDoc) =>
        updateDoc(doc(db, "services", serviceDoc.id), {
          unavailableDates: nextDates,
          unavailableSlots: nextSlots,
        }),
      ),
    );
    setMyServices((prev) =>
      prev.map((service) => ({
        ...service,
        unavailableDates: nextDates,
        unavailableSlots: nextSlots,
      })),
    );
  };

  const addUnavailableDate = async () => {
    if (!providerId || !unavailableDate) return;
    try {
      const next = [...new Set([...unavailableDates, unavailableDate])].sort();
      await updateDoc(doc(db, "users", providerId), { unavailableDates: next });
      await syncAvailabilityToServices(next, unavailableSlots);
      setUnavailableDates(next);
      setUnavailableDate("");
    } catch (error) {
      alert(error.message || "Could not save unavailable date.");
    }
  };

  const removeUnavailableDate = async (date) => {
    if (!providerId) return;
    try {
      const next = unavailableDates.filter((d) => d !== date);
      await updateDoc(doc(db, "users", providerId), { unavailableDates: next });
      await syncAvailabilityToServices(next, unavailableSlots);
      setUnavailableDates(next);
    } catch (error) {
      alert(error.message || "Could not remove unavailable date.");
    }
  };

  const addUnavailableSlot = async () => {
    if (!providerId || !unavailableSlotDate || !unavailableSlotValue) return;
    const key = buildDateSlotKey(unavailableSlotDate, unavailableSlotValue);
    if (!key) return;
    try {
      const next = [...new Set([...unavailableSlots, key])].sort();
      await updateDoc(doc(db, "users", providerId), { unavailableSlots: next });
      await syncAvailabilityToServices(unavailableDates, next);
      setUnavailableSlots(next);
    } catch (error) {
      alert(error.message || "Could not save unavailable slot.");
    }
  };

  const removeUnavailableSlot = async (slotKey) => {
    if (!providerId) return;
    try {
      const next = unavailableSlots.filter((value) => value !== slotKey);
      await updateDoc(doc(db, "users", providerId), { unavailableSlots: next });
      await syncAvailabilityToServices(unavailableDates, next);
      setUnavailableSlots(next);
    } catch (error) {
      alert(error.message || "Could not remove unavailable slot.");
    }
  };

  const saveProfileSettings = async () => {
    if (!providerId) return;
    await updateDoc(doc(db, "users", providerId), {
      displayName: String(profileDraft.displayName || "").trim(),
      businessName: String(profileDraft.businessName || "").trim(),
      about: String(profileDraft.about || "").trim(),
      primaryCategory: String(profileDraft.primaryCategory || "").trim(),
      yearsExperience: String(profileDraft.yearsExperience || "").trim(),
      avgResponseMinutes: String(profileDraft.avgResponseMinutes || "").trim(),
    });
    alert("Profile settings saved.");
    setReloadKey((v) => v + 1);
  };

  const requestFeaturedListing = async () => {
    if (!providerId) return;
    if (!featuredServiceId) {
      alert("Select a service first.");
      return;
    }
    const target = myServices.find((s) => s.id === featuredServiceId);
    if (!target) {
      alert("Selected service not found.");
      return;
    }
    try {
      setRequestingFeatured(true);
      const existing = await getDocs(query(
        collection(db, "featuredRequests"),
        where("providerId", "==", providerId),
      ));
      const duplicatePending = existing.docs.some((d) => {
        const data = d.data() || {};
        return data.serviceId === target.id && data.status === "pending";
      });
      if (duplicatePending) {
        alert("A pending featured request already exists for this service.");
        return;
      }
      await addDoc(collection(db, "featuredRequests"), {
        providerId,
        providerEmail: email || "",
        serviceId: target.id,
        serviceTitle: target.title || "",
        city: target.city || "",
        category: target.category || "",
        status: "pending",
        createdAt: new Date(),
      });
      alert("Featured listing request submitted.");
      setReloadKey((v) => v + 1);
    } catch (err) {
      alert(err.message);
    } finally {
      setRequestingFeatured(false);
    }
  };

  const selectedFeaturedService = myServices.find((item) => item.id === featuredServiceId) || null;
  const selectedFeaturedUntilMs = Date.parse(String(selectedFeaturedService?.featuredUntil || ""));
  const selectedFeaturedActive = Boolean(
    selectedFeaturedService?.isFeatured &&
    Number.isFinite(selectedFeaturedUntilMs) &&
    selectedFeaturedUntilMs > Date.now(),
  );
  const categoryCandidates = [
    profileDraft.primaryCategory,
    providerDocData?.primaryCategory,
    providerDocData?.partnerProfile?.serviceDetails?.category,
  ]
    .map((value) => String(value || "").toLowerCase())
    .filter(Boolean);
  const isVeterinaryProvider =
    categoryCandidates.some((value) => value.includes("veterinary")) ||
    myServices.some((service) => String(service.category || "").toLowerCase().includes("veterinary"));
  const checklistItems = [
    { key: "profileComplete", label: "Complete public profile (name, business, about)" },
    { key: "serviceAdded", label: "Add at least one service" },
    { key: "availabilityUpdated", label: "Set unavailable dates (recommended)" },
    { key: "approved", label: "Get admin approval" },
  ];
  const checklistProgress = checklistItems.filter((item) => checklist[item.key]).length;
  const dashboardHeroImage = (
    myServices.find((item) => Array.isArray(item.imageUrls) && item.imageUrls[0])?.imageUrls?.[0]
  ) || "/petaar-logo.jpeg";
  const statusTone =
    status === "approved" ? "success" :
      status === "rejected" ? "danger" :
        "warning";
  const primaryBtn = {
    padding: "12px 18px",
    borderRadius: 999,
    border: "1px solid var(--primary)",
    background: "linear-gradient(180deg, #365fd4 0%, #1f48be 100%)",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: 14,
    boxShadow: "0 14px 28px rgba(31, 72, 190, 0.18)",
    whiteSpace: "nowrap",
  };
  const accentBtn = {
    padding: "12px 18px",
    borderRadius: 999,
    border: "1px solid #b08900",
    background: "linear-gradient(180deg, #f7d765 0%, #f0c93d 100%)",
    color: "#1f3f99",
    fontWeight: 800,
    fontSize: 14,
    boxShadow: "0 14px 28px rgba(240, 201, 61, 0.22)",
    whiteSpace: "nowrap",
  };
  const metricCards = [
    { label: "Total Services", value: summary.totalServices, tone: "default" },
    { label: "Live Services", value: summary.liveServices, tone: "success" },
    { label: "Active Bookings", value: summary.activeBookings, tone: "accent" },
    { label: "Completed Bookings", value: summary.completedBookings, tone: "default" },
    { label: "Gross Collected", value: summary.grossCollected, prefix: "Rs ", tone: "success" },
    { label: "Platform Fee (20%)", value: summary.platformCommission, prefix: "Rs ", tone: "warning" },
    { label: "Est. Payout (80%)", value: summary.estimatedPayout, prefix: "Rs ", tone: "success" },
    {
      label: "Average Rating",
      value: summary.reviewCount > 0 ? summary.avgRating : 0,
      suffix: "/5",
      decimals: 1,
      tone: "accent",
      meta: summary.reviewCount > 0 ? `${summary.reviewCount} reviews` : "No reviews yet",
    },
  ];
  const checklistLabel = `${checklistProgress}/${checklistItems.length}`;

  return (
    <div className="petaar-page">
      <div
        style={{ maxWidth: 1260, margin: "0 auto", display: "grid", gap: 20 }}
      >
        <section
          className="petaar-card"
          style={{
            background:
              "radial-gradient(900px 520px at 92% 48%, rgba(255,255,255,0.9), rgba(255,255,255,0.18) 54%), radial-gradient(700px 380px at 100% 18%, rgba(146,177,247,0.26), transparent 68%), linear-gradient(135deg, #ecf2ff 0%, #e7eefb 46%, #f0f4ff 100%)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <PageHeader
              eyebrow="Provider Workspace"
              title="Service Provider Dashboard"
              subtitle="Manage listing quality, availability, bookings, featured placement, and vet verification from one screen."
            />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={() => setReloadKey((v) => v + 1)} disabled={loading} style={accentBtn}>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
              <button onClick={onLogout} style={primaryBtn}>
                Logout
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
            <ToneBadge tone={statusTone}>Status: {status}</ToneBadge>
            {profileDraft.primaryCategory ? (
              <ToneBadge tone="default">Primary Category: {profileDraft.primaryCategory}</ToneBadge>
            ) : null}
            <ToneBadge tone="accent">{email}</ToneBadge>
          </div>
          {!canAddService && (
            <p style={{ margin: "12px 0 0", color: "#92400e" }}>
              Your profile is not approved yet. Services remain hidden until admin approval.
            </p>
          )}
          {loadError && (
            <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", padding: 10, borderRadius: 8, marginTop: 10 }}>
              {loadError}
            </div>
          )}
          {loading && <p style={{ marginTop: 8 }}>Loading dashboard data...</p>}

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "nowrap",
              overflowX: "auto",
              paddingBottom: 4,
              marginTop: 14,
            }}
          >
            <button onClick={canAddService ? onAddService : undefined} disabled={!canAddService} style={{ ...primaryBtn, flex: "0 0 auto" }}>Add Service</button>
            <button onClick={onMyServices} style={{ ...primaryBtn, flex: "0 0 auto" }}>My Services</button>
            <button onClick={onBookings} style={{ ...primaryBtn, flex: "0 0 auto" }}>Bookings</button>
            <button onClick={onEarnings} style={{ ...accentBtn, flex: "0 0 auto" }}>Earnings</button>
            <button onClick={onPayouts} style={{ ...primaryBtn, flex: "0 0 auto" }}>Payouts</button>
            <button onClick={onReviews} style={{ ...primaryBtn, flex: "0 0 auto" }}>Reviews</button>
            <button onClick={onMarketplace} style={{ ...primaryBtn, flex: "0 0 auto" }}>Marketplace Hub</button>
            <button onClick={onSupport} style={{ ...primaryBtn, flex: "0 0 auto" }}>Support Tickets</button>
          </div>

          <div className="petaar-stat-grid" style={{ marginTop: 16 }}>
            {metricCards.map((item) => (
              <StatCard
                key={item.label}
                label={item.label}
                value={item.value}
                prefix={item.prefix || ""}
                suffix={item.suffix || ""}
                decimals={item.decimals || 0}
                meta={item.meta || ""}
                tone={item.tone || "default"}
              />
            ))}
          </div>
        </section>

        <section
          style={{
            marginTop: 10,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 10,
          }}
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div className="petaar-card">
              <h3 style={{ marginTop: 0, marginBottom: 6, color: "#24356b" }}>Onboarding Checklist</h3>
              <div style={{ display: "grid", gap: 6 }}>
                {checklistItems.map((item) => (
                  <div key={item.key} style={{ display: "flex", gap: 8, alignItems: "center", color: "#334155" }}>
                    <ToneBadge tone={checklist[item.key] ? "success" : "default"}>
                      {checklist[item.key] ? "Done" : "Pending"}
                    </ToneBadge>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
                <div style={{ background: "#d4e2ff", borderRadius: 999, height: 10 }}>
                  <div
                    style={{
                      width: `${(checklistProgress / checklistItems.length) * 100}%`,
                      height: "100%",
                      borderRadius: 999,
                      background: "linear-gradient(90deg, #3b82f6, #60a5fa)",
                    }}
                  />
                </div>
                <small style={{ color: "#475569", fontWeight: 700 }}>
                  {checklistLabel}
                </small>
              </div>

              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 10 }}>
                <div className="petaar-card" style={{ padding: 16 }}>
                  <strong style={{ color: "#24356b" }}>Unavailable Dates</strong>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginTop: 8 }}>
                    <input type="date" value={unavailableDate} onChange={(e) => setUnavailableDate(e.target.value)} />
                    <button onClick={addUnavailableDate} style={primaryBtn}>Add Unavailable Date</button>
                  </div>
                  <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {unavailableDates.length === 0 ? (
                      <small style={{ color: "#94a3b8" }}>No unavailable dates added.</small>
                    ) : (
                      unavailableDates.map((d) => (
                        <button key={d} onClick={() => removeUnavailableDate(d)} style={{ borderRadius: 999, padding: "6px 12px" }}>
                          {d} x
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="petaar-card" style={{ padding: 16 }}>
                  <strong style={{ color: "#24356b" }}>Unavailable Slots</strong>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginTop: 8 }}>
                    <input type="date" value={unavailableSlotDate} onChange={(e) => setUnavailableSlotDate(e.target.value)} />
                    <select value={unavailableSlotValue} onChange={(e) => setUnavailableSlotValue(e.target.value)}>
                      {BOOKING_SLOT_OPTIONS.map((slot) => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                    <button onClick={addUnavailableSlot} style={primaryBtn}>Add Unavailable Slot</button>
                  </div>
                  <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {unavailableSlots.length === 0 ? (
                      <small style={{ color: "#94a3b8" }}>No unavailable slots added.</small>
                    ) : (
                      unavailableSlots.map((slotKey) => {
                        const parsed = parseDateSlotKey(slotKey);
                        const prettySlot = BOOKING_SLOT_OPTIONS.find(
                          (slot) => slot.toLowerCase() === String(parsed.slot || "").toLowerCase(),
                        ) || parsed.slot;
                        const label = parsed.date && prettySlot ? `${parsed.date} | ${prettySlot}` : slotKey;
                        return (
                          <button key={slotKey} onClick={() => removeUnavailableSlot(slotKey)} style={{ borderRadius: 999, padding: "6px 12px" }}>
                            {label} x
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="petaar-card" style={{ marginTop: 10, padding: 16 }}>
                <h3 style={{ marginTop: 0, marginBottom: 8, color: "#24356b" }}>Public Profile Settings</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))", gap: 8 }}>
                  <input
                    placeholder="Display Name"
                    value={profileDraft.displayName}
                    onChange={(e) => setProfileDraft((prev) => ({ ...prev, displayName: e.target.value }))}
                    style={{ padding: 9 }}
                  />
                  <input
                    placeholder="Business Name"
                    value={profileDraft.businessName}
                    onChange={(e) => setProfileDraft((prev) => ({ ...prev, businessName: e.target.value }))}
                    style={{ padding: 9 }}
                  />
                </div>
                <textarea
                  placeholder="About your service"
                  value={profileDraft.about}
                  onChange={(e) => setProfileDraft((prev) => ({ ...prev, about: e.target.value }))}
                  style={{ display: "block", marginTop: 8, width: "100%", minHeight: 82, padding: 9 }}
                />
                <select
                  value={profileDraft.primaryCategory}
                  onChange={(e) => setProfileDraft((prev) => ({ ...prev, primaryCategory: e.target.value }))}
                  style={{ display: "block", marginTop: 8, width: "100%", padding: 9 }}
                >
                  <option value="">Primary Service Category</option>
                  {SERVICE_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))", gap: 8, marginTop: 8 }}>
                  <input
                    placeholder="Years Experience (e.g. 8+ years)"
                    value={profileDraft.yearsExperience}
                    onChange={(e) => setProfileDraft((prev) => ({ ...prev, yearsExperience: e.target.value }))}
                    style={{ padding: 9 }}
                  />
                  <input
                    placeholder="Avg Response Minutes (e.g. 30)"
                    value={profileDraft.avgResponseMinutes}
                    onChange={(e) => setProfileDraft((prev) => ({ ...prev, avgResponseMinutes: e.target.value }))}
                    style={{ padding: 9 }}
                  />
                </div>
                <button onClick={saveProfileSettings} style={{ ...primaryBtn, marginTop: 10 }}>Save Profile Settings</button>
                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
                  <div style={{ background: "#d4e2ff", borderRadius: 999, height: 12 }}>
                    <div
                      style={{
                        width: `${(checklistProgress / checklistItems.length) * 100}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #3b82f6, #60a5fa)",
                      }}
                    />
                  </div>
                  <small style={{ color: "#475569", fontWeight: 700 }}>{checklistLabel}</small>
                </div>
              </div>
            </div>
          </div>

          <aside className="petaar-card" style={{ display: "grid", alignContent: "space-between", gap: 12, minHeight: 380 }}>
            <div>
              <h3 style={{ marginTop: 0, color: "#24356b" }}>Featured Listing (Monthly)</h3>
              <select value={featuredServiceId} onChange={(e) => setFeaturedServiceId(e.target.value)} style={{ width: "100%", padding: 8 }}>
                <option value="">Select Service</option>
                {myServices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title} ({s.city || "N/A"})
                  </option>
                ))}
              </select>
              {selectedFeaturedService ? (
                <p style={{ marginBottom: 8 }}>
                  Current status for selected service:{" "}
                  <strong>{selectedFeaturedActive ? "Featured Active" : "Not Featured"}</strong>
                  {selectedFeaturedActive ? ` (till ${new Date(selectedFeaturedUntilMs).toLocaleDateString("en-IN")})` : ""}
                </p>
              ) : null}
              <SubscriptionCheckoutButton
                planCode="featured_monthly"
                serviceId={featuredServiceId}
                disabled={!featuredServiceId}
                buttonLabel="Activate Featured (₹999 / 30 days)"
                onSuccess={() => {
                  setReloadKey((v) => v + 1);
                }}
              />
              <p style={{ marginTop: 10, marginBottom: 6, color: "#64748b" }}>
                Optional: request manual approval if needed.
              </p>
              <button onClick={requestFeaturedListing} disabled={requestingFeatured} style={accentBtn}>
                {requestingFeatured ? "Submitting..." : "Request Featured Listing"}
              </button>
            </div>
            <div style={{ alignSelf: "end", display: "flex", justifyContent: "center" }}>
              <img
                src={dashboardHeroImage}
                alt="Provider pets"
                style={{ width: "100%", maxWidth: 260, maxHeight: 180, objectFit: "contain", borderRadius: 12 }}
              />
            </div>
          </aside>
        </section>

        {isVeterinaryProvider ? (
          <div style={{ marginTop: 10 }}>
            <VetOnboardingPanel
              userProfile={providerDocData}
              onUpdated={() => setReloadKey((v) => v + 1)}
            />
          </div>
        ) : (
          <EmptyState
            icon="🩺"
            title="Vet onboarding appears only for veterinary providers"
            description="Once your category or service listing is veterinary-focused, the verification and consultation setup panel will show here."
          />
        )}
      </div>
    </div>
  );
}
